import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiscussionHistoryEntry, discussionService } from '../services/api';
import { Discussion } from '../types';
import { formatLocalTimestamp } from '../utils/discussionTime';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', in_review: 'In review', ready: 'Ready', published: 'Published', archived: 'Archived',
};

function describe(entry: DiscussionHistoryEntry) {
  const to = STATUS_LABELS[entry.to_status || ''] || entry.to_status;
  const from = STATUS_LABELS[entry.from_status || ''] || entry.from_status;
  switch (entry.action) {
    case 'created': return `Created as ${to}`;
    case 'status_changed': return `Moved from ${from} to ${to}`;
    case 'edited': return 'Edited';
    case 'expired': return 'Expired (valid time ended)';
    case 'note': return 'Note';
    default: return entry.action;
  }
}

interface Props {
  discussion: Discussion;
  canCreate: boolean;
}

/** Staff-only tools under a discussion: area check, exports, duplicate, history and reviewer notes. */
const DiscussionStaffPanel: React.FC<Props> = ({ discussion, canCreate }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState<DiscussionHistoryEntry[]>([]);
  const [note, setNote] = useState('');
  const [exportText, setExportText] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<'text' | 'x'>('text');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadHistory = useCallback(() => {
    discussionService.history(discussion.id).then((res) => setHistory(res.data)).catch(() => setHistory([]));
  }, [discussion.id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const showExport = async (format: 'text' | 'x') => {
    setError(null);
    try {
      const res = await discussionService.exportText(discussion.id, format);
      setExportFormat(format);
      setExportText(res.data.text);
      setCopied(false);
    } catch {
      setError('Unable to build export.');
    }
  };

  const copyExport = async () => {
    if (!exportText) return;
    await navigator.clipboard.writeText(exportText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const duplicate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await discussionService.duplicate(discussion.id);
      navigate(`/discussions/${res.data.permalink}`);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Unable to duplicate discussion.');
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await discussionService.addNote(discussion.id, note.trim());
      setNote('');
      loadHistory();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Unable to save note.');
    } finally {
      setBusy(false);
    }
  };

  const areaCheck = discussion.area_check;

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <p className="eyebrow">Staff tools</p>
      {error && <div className="alert alert-error">{error}</div>}

      {areaCheck && (
        <div className="detail-chip-groups">
          <div>
            <p className="detail-subheading">Area check</p>
            <p className="field-help">States in area: {areaCheck.states.length ? areaCheck.states.join(', ') : 'none of WI, MI, OH, IL, IN'}</p>
            {areaCheck.warnings.map((warning) => <div key={warning} className="alert alert-error">{warning}</div>)}
          </div>
        </div>
      )}

      <div className="hero-actions">
        <button type="button" className="btn btn-secondary" onClick={() => showExport('text')}>GLSN text product</button>
        <button type="button" className="btn btn-secondary" onClick={() => showExport('x')}>X post text</button>
        {canCreate && (
          <button type="button" className="btn btn-secondary" onClick={duplicate} disabled={busy}>Duplicate as new draft</button>
        )}
      </div>

      {exportText !== null && (
        <div className="form-group" style={{ marginTop: '0.75rem' }}>
          <label className="form-label" htmlFor="export-text">
            {exportFormat === 'x' ? `X post (${exportText.length} characters)` : 'GLSN plain-text product'}
          </label>
          <textarea id="export-text" className="form-input export-box" readOnly value={exportText} />
          <div className="action-row">
            <button type="button" className="btn btn-primary" onClick={copyExport}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </div>
      )}

      <div className="detail-chip-groups">
        <div style={{ width: '100%' }}>
          <p className="detail-subheading">History and review notes</p>
          {history.length === 0 ? (
            <p className="field-help">No history recorded yet.</p>
          ) : (
            <ul className="history-list">
              {history.map((entry) => (
                <li key={entry.id} className={`history-item ${entry.action === 'note' ? 'history-item-note' : ''}`}>
                  <div><strong>{describe(entry)}</strong></div>
                  <div className="history-meta">{entry.actor_name || 'System'} · {formatLocalTimestamp(entry.created_at)}</div>
                  {entry.note && <p className="history-note">{entry.note}</p>}
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={addNote} style={{ marginTop: '0.75rem' }}>
            <label className="form-label" htmlFor="review-note">Add a note</label>
            <textarea id="review-note" className="form-input" rows={3} maxLength={2000} value={note}
              onChange={(e) => setNote(e.target.value)} placeholder="Feedback for the author or other reviewers" />
            <div className="action-row">
              <button type="submit" className="btn btn-secondary" disabled={busy || !note.trim()}>Save note</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DiscussionStaffPanel;
